import { useTranslation } from 'react-i18next'

import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import { useFoundations } from '@percent/admin-dashboard/common/hooks'
import { DisbursementsFiltersProps } from './DisbursementsFilters.types'
import {
  PAID_BY_LABEL_KEY,
  PAID_BY_ORDER,
  PAID_BY_TO_DISBURSEMENT_TYPE,
  STATUS_FILTER_TYPE,
  STATUS_LABEL_KEY
} from './disbursementsFilterOptions'

export type DisbursementFilterChip = {
  id: string
  label: string
  value: string
  onRemove: VoidFunction
}

/** Matches the chips in the design: "Jan 1, 2025 - Jan 1, 2026". */
const formatRange = (from: string, to: string) =>
  `${dayJS(from).format('MMM D, YYYY')} - ${dayJS(to).format('MMM D, YYYY')}`

/**
 * One chip per active filter, so what is filtering the list is visible without opening each pill.
 * Search is deliberately absent: the term is already shown in the input, which has its own clear.
 */
export const useDisbursementFilterChips = ({
  queryParams,
  setQueryParams
}: DisbursementsFiltersProps): DisbursementFilterChip[] => {
  const { t } = useTranslation()
  const { foundations } = useFoundations()

  const chips: DisbursementFilterChip[] = []

  const { foundationId, anyDisbursementsWithTypes, anyDisbursementsWithStatus, allDisbursementsWithStatus } =
    queryParams

  if (foundationId) {
    chips.push({
      id: 'foundation',
      label: t('dropdown.foundation'),
      // The id is a poor thing to show, but it beats an empty chip while the list is still loading.
      value: foundations?.[foundationId]?.name ?? foundationId,
      onRemove: () => setQueryParams({ ...queryParams, foundationId: undefined })
    })
  }

  if (anyDisbursementsWithTypes?.length) {
    const selected = PAID_BY_ORDER.filter(option =>
      anyDisbursementsWithTypes.includes(PAID_BY_TO_DISBURSEMENT_TYPE[option])
    )

    chips.push({
      id: 'paidBy',
      label: t('table.paidBy'),
      value: selected.map(option => t(PAID_BY_LABEL_KEY[option])).join(', '),
      onRemove: () =>
        setQueryParams({ ...queryParams, anyDisbursementsWithTypes: undefined, allDisbursementsWithTypes: undefined })
    })
  }

  // The two status params are mutually exclusive server-side, so at most one of them is set.
  const getStatus = () => {
    if (anyDisbursementsWithStatus?.length) {
      return STATUS_FILTER_TYPE.ACTION_REQUIRED
    }

    if (allDisbursementsWithStatus?.length) {
      return STATUS_FILTER_TYPE.NO_ACTION_REQUIRED
    }

    return undefined
  }

  const status = getStatus()

  if (status) {
    chips.push({
      id: 'status',
      label: t('dropdown.status.title'),
      value: t(STATUS_LABEL_KEY[status]),
      onRemove: () =>
        setQueryParams({ ...queryParams, anyDisbursementsWithStatus: undefined, allDisbursementsWithStatus: undefined })
    })
  }

  // Only complete ranges: a lone bound is dropped before the request, so chipping it would offer to
  // remove a filter that is not applied.
  if (queryParams.createdAtFrom && queryParams.createdAtTo) {
    chips.push({
      id: 'createdAt',
      label: t('table.createdDate'),
      value: formatRange(queryParams.createdAtFrom, queryParams.createdAtTo),
      onRemove: () => setQueryParams({ ...queryParams, createdAtFrom: undefined, createdAtTo: undefined })
    })
  }

  if (queryParams.endDateFrom && queryParams.endDateTo) {
    chips.push({
      id: 'endDate',
      label: t('table.upTo'),
      value: formatRange(queryParams.endDateFrom, queryParams.endDateTo),
      onRemove: () => setQueryParams({ ...queryParams, endDateFrom: undefined, endDateTo: undefined })
    })
  }

  return chips
}
